<template>
  <div class="account-container">
    <!-- Toast Notification -->
    <Teleport to="body">
      <Transition name="toast">
        <div v-if="paymentMessage" :class="['toast-notification', paymentStatus]">
          <i :class="paymentStatus === 'success' ? 'fa fa-check-circle' : 'fa fa-times-circle'"></i>
          <span>{{ paymentMessage }}</span>
          <button class="toast-close" @click="dismissToast">&times;</button>
        </div>
      </Transition>
    </Teleport>

    <!-- Get More Credits and referral only -->
    <div class="account-customize">
      <!-- Pricing Section -->
      <div class="page-section">
      <h2>Get More Credits</h2>
      <p class="page-section-subtitle">Choose a credit package that fits your job search needs</p>
      
      <div class="pricing-grid">
        <div class="pricing-card">
          <div class="pricing-header">
            <h3>Starter</h3>
            <p class="pricing-desc">Try it out</p>
          </div>
          <div class="pricing-price">
            <span class="price">$2.99</span>
          </div>
          <ul class="pricing-features">
            <li><i class="fa fa-check"></i> 15 resume credits</li>
            <li><i class="fa fa-check"></i> PDF, DOCX, HTML downloads</li>
            <li><i class="fa fa-check"></i> AI-powered optimization</li>
          </ul>
          <button class="btn btn-secondary" @click="purchaseCredits('starter')" :disabled="purchasing">
            {{ purchasing === 'starter' ? 'Processing...' : 'Get Started' }}
          </button>
        </div>

        <div class="pricing-card featured">
          <div class="featured-badge">Most Popular</div>
          <div class="pricing-header">
            <h3>Standard</h3>
            <p class="pricing-desc">For active job seekers</p>
          </div>
          <div class="pricing-price">
            <span class="price">$6.99</span>
          </div>
          <ul class="pricing-features">
            <li><i class="fa fa-check"></i> 50 resume credits</li>
            <li><i class="fa fa-check"></i> PDF, DOCX, HTML downloads</li>
            <li><i class="fa fa-check"></i> AI-powered optimization</li>
            <li><i class="fa fa-check"></i> Best value per resume</li>
          </ul>
          <button class="btn btn-primary" @click="purchaseCredits('standard')" :disabled="purchasing">
            {{ purchasing === 'standard' ? 'Processing...' : 'Get Standard' }}
          </button>
        </div>

        <div class="pricing-card">
          <div class="pricing-header">
            <h3>Pro</h3>
            <p class="pricing-desc">For serious job hunters</p>
          </div>
          <div class="pricing-price">
            <span class="price">$14.99</span>
          </div>
          <ul class="pricing-features">
            <li><i class="fa fa-check"></i> 150 resume credits</li>
            <li><i class="fa fa-check"></i> PDF, DOCX, HTML downloads</li>
            <li><i class="fa fa-check"></i> AI-powered optimization</li>
            <li><i class="fa fa-check"></i> Lowest cost per resume</li>
          </ul>
          <button class="btn btn-secondary" @click="purchaseCredits('pro')" :disabled="purchasing">
            {{ purchasing === 'pro' ? 'Processing...' : 'Get Pro' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Referral Section -->
    <div class="section referral-section">
      <div class="referral-header">
        <div class="referral-icon">
          <i class="fas fa-users"></i>
        </div>
        <div>
          <h2>Refer Friends, Earn Credits</h2>
          <p class="section-subtitle">Get <span class="highlight">10 free resume credits</span> for each friend who signs up and makes their first purchase!</p>
        </div>
      </div>
      
      <div class="referral-card">
        <div class="referral-link-section">
          <label>Your referral link</label>
          <div class="referral-link-box">
            <input 
              type="text" 
              :value="referralLink" 
              readonly 
              ref="referralInput"
            />
            <button class="copy-btn" @click="copyReferralLink">
              <i :class="copied ? 'fa fa-check' : 'fa fa-copy'"></i>
              {{ copied ? 'Copied!' : 'Copy' }}
            </button>
          </div>
        </div>

        <div class="referral-stats">
          <div class="stat-item">
            <span class="stat-number">{{ referralStats.totalReferrals }}</span>
            <span class="stat-label">Total Referrals</span>
          </div>
          <div class="stat-item">
            <span class="stat-number">{{ referralStats.qualifiedReferrals }}</span>
            <span class="stat-label">Qualified</span>
          </div>
          <div class="stat-item highlight">
            <span class="stat-number">{{ referralStats.totalCreditsEarned }}</span>
            <span class="stat-label">Credits Earned</span>
          </div>
        </div>

        <div class="share-buttons">
          <button class="share-btn twitter" @click="shareOnTwitter">
            <i class="fab fa-x-twitter"></i><span>Share on X</span>
          </button>
          <button class="share-btn linkedin" @click="shareOnLinkedIn">
            <i class="fab fa-linkedin"></i><span>Share on LinkedIn</span>
          </button>
          <button class="share-btn email" @click="shareViaEmail">
            <i class="fa fa-envelope"></i><span>Send Email</span>
          </button>
        </div>
      </div>

      <!-- How it works -->
      <div class="how-it-works">
        <h4>How it works</h4>
        <div class="steps">
          <div class="step">
            <div class="step-number">1</div>
            <p>Share your unique link with friends</p>
          </div>
          <div class="step">
            <div class="step-number">2</div>
            <p>They sign up and make a purchase</p>
          </div>
          <div class="step">
            <div class="step-number">3</div>
            <p>You get 10 free resume credits!</p>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import axios, { formatApiErrorMessage } from "@/axios";

export default {
  name: "AccountComponent",
  components: {},
  props: {
    credits: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      localCredits: 0,
      referralCode: '',
      referralStats: {
        totalReferrals: 0,
        pendingReferrals: 0,
        qualifiedReferrals: 0,
        totalCreditsEarned: 0,
      },
      copied: false,
      purchasing: null,
      paymentMessage: '',
      paymentStatus: '',
      prices: null,
    };
  },
  computed: {
    referralLink() {
      return `https://resymaid.com/register?ref=${this.referralCode}`;
    },
    displayCredits() {
      return this.credits !== null ? this.credits : this.localCredits;
    }
  },
  methods: {
    async fetchReferralInfo() {
      try {
        const response = await axios.get('/api/referral/info');
        this.referralCode = response.data.referralCode;
        this.referralStats = response.data.stats;
      } catch (error) {
        console.error('Error fetching referral info:', error);
        // If user not found, session may be invalid - don't show error, just use defaults
        if (error.response?.status === 404) {
          this.referralCode = '';
        }
      }
    },
    async fetchPrices() {
      try {
        const response = await axios.get('/api/stripe/prices');
        this.prices = response.data.prices;
      } catch (error) {
        console.error('Error fetching prices:', error);
        // Don't show this error to users - prices will use defaults
      }
    },
    copyReferralLink() {
      navigator.clipboard.writeText(this.referralLink);
      this.copied = true;
      setTimeout(() => {
        this.copied = false;
      }, 2000);
    },
    shareOnTwitter() {
      const text = `I'm using ResyMaid to create AI-powered resumes tailored to each job. Sign up with my link and we both get rewards! 🚀`;
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(this.referralLink)}`;
      window.open(url, '_blank');
    },
    shareOnLinkedIn() {
      const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(this.referralLink)}`;
      window.open(url, '_blank');
    },
    shareViaEmail() {
      const subject = 'Check out ResyMaid - AI Resume Builder';
      const body = `Hey!\n\nI've been using ResyMaid to create AI-powered resumes tailored to each job application. It's been a game-changer for my job search!\n\nSign up using my referral link and make your first purchase - we both get 10 free resume credits!\n\n${this.referralLink}\n\nBest,`;
      window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    },
    async purchaseCredits(tier) {
      if (!this.prices) {
        await this.fetchPrices();
      }

      // Find the price ID for the selected tier
      const tierMap = {
        starter: 'Starter',
        standard: 'Standard',
        pro: 'Pro'
      };
      
      const price = this.prices?.find(p => p.name === tierMap[tier]);
      
      if (!price || !price.id) {
        this.paymentStatus = 'error';
        this.paymentMessage = 'Unable to process payment. Please try again later.';
        return;
      }

      this.purchasing = tier;
      this.paymentMessage = '';

      try {
        const response = await axios.post('/api/stripe/create-checkout-session', {
          priceId: price.id
        });

        // Redirect to Stripe Checkout
        if (response.data.url) {
          window.location.href = response.data.url;
        } else {
          throw new Error('No checkout URL returned');
        }
      } catch (error) {
        console.error('Payment error:', error);
        this.paymentStatus = 'error';
        this.paymentMessage = formatApiErrorMessage(error, 'Failed to initiate payment. Please try again.');
        this.purchasing = null;
      }
    },
    checkPaymentStatus() {
      const urlParams = new URLSearchParams(window.location.search);
      const payment = urlParams.get('payment');
      
      if (payment === 'success') {
        this.paymentStatus = 'success';
        this.paymentMessage = 'Payment successful! Your credits have been added to your account.';
        // Emit event to parent to refresh credits
        this.$emit('refresh-credits');
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
        // Auto-dismiss toast after 5 seconds
        this.scheduleToastDismiss();
      } else if (payment === 'cancelled') {
        this.paymentStatus = 'error';
        this.paymentMessage = 'Payment was cancelled. No charges were made.';
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
        // Auto-dismiss toast after 5 seconds
        this.scheduleToastDismiss();
      }
    },
    dismissToast() {
      this.paymentMessage = '';
      this.paymentStatus = '';
    },
    scheduleToastDismiss() {
      setTimeout(() => {
        this.dismissToast();
      }, 5000);
    },
    async fetchCredits() {
      try {
        const response = await axios.get("/api/auth/me");
        this.localCredits = response.data.user?.credits || 0;
        console.log("Fetched credits:", this.localCredits);
      } catch (error) {
        console.error("Error fetching credits:", error);
      }
    }
  },
  async mounted() {
    // If no credits prop from parent, fetch directly
    if (this.credits === null) {
      await this.fetchCredits();
    }
    this.fetchReferralInfo();
    this.fetchPrices();
    this.checkPaymentStatus();
  }
};
</script>

<!-- Global styles for toast (teleported outside component) -->
<style>
.toast-notification {
  position: fixed;
  top: 24px;
  right: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-radius: 12px;
  font-weight: 500;
  z-index: 9999;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  max-width: 400px;
}

.toast-notification.success {
  background: linear-gradient(135deg, #065f46 0%, #047857 100%);
  border: 1px solid rgba(74, 222, 128, 0.3);
  color: #ecfdf5;
}

.toast-notification.error {
  background: linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%);
  border: 1px solid rgba(248, 113, 113, 0.3);
  color: #fef2f2;
}

.toast-notification i {
  font-size: 20px;
}

.toast-notification span {
  flex: 1;
}

.toast-close {
  background: none;
  border: none;
  color: inherit;
  font-size: 20px;
  cursor: pointer;
  opacity: 0.7;
  padding: 0 0 0 8px;
  line-height: 1;
}

.toast-close:hover {
  opacity: 1;
}

/* Toast animation */
.toast-enter-active {
  animation: toast-in 0.3s ease-out;
}

.toast-leave-active {
  animation: toast-out 0.3s ease-in;
}

@keyframes toast-in {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes toast-out {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(100%);
  }
}
</style>

<style scoped>
.account-container {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.account-container * {
  box-sizing: border-box;
}

/* Tabs */
.account-tabs {
  display: flex;
  align-items: center;
  gap: 0;
  margin-bottom: 24px;
  position: relative;
}

.account-tab {
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 500;
  color: var(--app-text-secondary);
  background: transparent;
  border: 1px solid transparent;
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  cursor: pointer;
  transition: color 0.2s, background 0.2s;
}

.account-tab:hover {
  color: var(--app-text);
}

.account-tab.active {
  color: var(--app-text);
  background: var(--app-surface);
  border-color: var(--app-border);
}

.account-settings-icon {
  margin-left: auto;
  padding: 8px 12px;
  color: var(--app-text-secondary);
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
}

.account-settings-icon:hover {
  color: var(--app-text);
}

/* Edit tab: two-column layout */
.account-edit-layout {
  display: flex;
  gap: 24px;
  min-height: 400px;
}

.account-form-panel {
  flex: 0 0 36%;
  max-width: 420px;
  background: var(--app-surface);
  border: 1px solid var(--app-border);
  border-radius: 12px;
  padding: 24px;
}

.resume-score-bar {
  margin-bottom: 8px;
}

.resume-score-text {
  font-size: 13px;
  font-weight: 600;
  color: var(--app-error);
}

.resume-score-track {
  height: 6px;
  background: var(--app-surface-hover);
  border-radius: 3px;
  overflow: hidden;
  margin-top: 6px;
}

.resume-score-fill {
  height: 100%;
  background: var(--app-error);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.resume-score-hint {
  margin: 0 0 20px;
  font-size: 13px;
}

.hint-green {
  color: var(--app-success);
}

.panel-title {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 700;
  color: var(--app-text);
}

.panel-subtitle {
  margin: 0 0 20px;
  font-size: 14px;
  color: var(--app-text-secondary);
  line-height: 1.5;
}

.account-form .form-group {
  margin-bottom: 18px;
}

.account-form .form-group .form-group-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.account-form .form-group > label,
.account-form .form-group label {
  font-size: 13px;
  font-weight: 600;
  color: var(--app-text);
  margin: 0;
}

.account-form .form-group > label {
  display: block;
  margin-bottom: 6px;
}

.account-form .form-group .field-hint {
  font-size: 12px;
  font-weight: 500;
  color: var(--app-success);
}

.account-form .form-group input {
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  color: var(--app-text);
  background: var(--app-bg);
  border: 1px solid var(--app-border);
  border-radius: 8px;
  box-sizing: border-box;
}

.account-form .form-group input::placeholder {
  color: var(--app-text-secondary);
  opacity: 0.8;
}

.upload-photo-placeholder {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px;
  background: var(--app-surface-hover);
  border: 1px dashed var(--app-border);
  border-radius: 8px;
  cursor: pointer;
  color: var(--app-text-secondary);
  font-size: 14px;
}

.upload-photo-placeholder:hover {
  border-color: var(--app-primary);
  color: var(--app-primary);
}

.upload-photo-placeholder i {
  font-size: 28px;
}

.hidden-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.btn-next {
  width: 100%;
  padding: 14px 20px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  background: var(--app-primary);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  margin-top: 8px;
  transition: filter 0.2s;
}

.btn-next:hover:not(:disabled) {
  filter: brightness(1.05);
}

.btn-next:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.save-feedback {
  margin: 8px 0 0;
  font-size: 13px;
}

.save-feedback.success {
  color: var(--app-success);
}

.save-feedback.error {
  color: var(--app-error);
}

.progress-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
}

.progress-dots .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--app-surface-hover);
  transition: background 0.2s;
}

.progress-dots .dot.active {
  background: var(--app-primary);
}

/* Right: Preview panel */
.account-preview-panel {
  flex: 1;
  min-width: 0;
  background: var(--app-bg);
  border: 1px solid var(--app-border);
  border-radius: 12px;
  display: flex;
  align-items: stretch;
}

.preview-placeholder {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 24px;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
}

.preview-name {
  font-size: 18px;
  font-weight: 600;
  color: var(--app-primary);
}

.preview-email {
  font-size: 14px;
  color: var(--app-primary);
  text-decoration: none;
}

.preview-email:hover {
  text-decoration: underline;
}

.preview-page-nav {
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 0 0;
  border-top: 1px solid var(--app-border);
}

.page-nav-btn {
  padding: 6px 10px;
  background: var(--app-surface);
  border: 1px solid var(--app-border);
  border-radius: 6px;
  color: var(--app-text-secondary);
  cursor: pointer;
  font-size: 12px;
}

.page-nav-btn:hover {
  color: var(--app-text);
}

.page-nav-label {
  font-size: 12px;
  color: var(--app-text-secondary);
}

.account-customize {
  width: 100%;
}

/* Sections – readable contrast on light background */
.section {
  margin-bottom: 40px;
}

.section h2 {
  color: var(--app-text);
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 4px;
}

.section-subtitle {
  color: var(--text-secondary);
  font-size: 15px;
  margin-bottom: 12px;
}

.section-subtitle .highlight {
  color: var(--app-primary);
  font-weight: 600;
}

/* Referral Section – single inset card on grouped bg */
.referral-section {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
  border-radius: var(--card-radius, 12px);
  padding: 20px;
  width: 100%;
  overflow: hidden;
  box-shadow: none;
}

.referral-header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 28px;
}

.referral-header > div:last-child {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.referral-header h2 {
  margin: 0 0 2px 0;
  color: var(--app-text);
  font-size: 18px;
  font-weight: 700;
}

.referral-header .section-subtitle {
  margin: 0;
  color: var(--app-text-secondary);
  font-size: 12px;
}

.referral-icon {
  width: 48px;
  height: 48px;
  background: var(--surface-3, #f2f2f7);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.referral-icon i {
  font-size: 20px;
  color: var(--app-primary);
}

.referral-card {
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0;
  margin-bottom: 24px;
  width: 100%;
  overflow: hidden;
  box-shadow: none;
}

.referral-link-section {
  margin-bottom: 24px;
}

.referral-link-section label {
  display: block;
  color: var(--app-text-secondary);
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 8px;
}

.referral-link-box {
  display: flex;
  gap: 12px;
}

.referral-link-box input {
  flex: 1;
  min-width: 0;
  background: var(--app-surface);
  border: 1px solid var(--app-border);
  border-radius: 10px;
  padding: 14px 16px;
  color: var(--app-text);
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.copy-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 20px;
  min-height: var(--control-height, 44px);
  background: var(--app-primary);
  border: none;
  border-radius: 10px;
  color: var(--app-primary-text);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  white-space: nowrap;
}

.copy-btn:hover {
  background: var(--app-primary-hover);
}

.referral-stats {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
}

.referral-card .stat-item {
  flex: 1;
  background: transparent;
  border: 1px solid var(--app-border);
  border-radius: var(--card-radius, 12px);
  padding: 16px;
  text-align: center;
  box-shadow: none;
}

.referral-card .stat-item.highlight {
  background: var(--fill-primary-soft, color-mix(in srgb, var(--app-primary) 12%, var(--app-surface)));
  border-color: color-mix(in srgb, var(--app-primary) 28%, var(--app-border));
}

.referral-card .stat-number {
  display: block;
  font-size: 28px;
  font-weight: 700;
  color: var(--app-text);
  margin-bottom: 4px;
}

.referral-card .stat-item.highlight .stat-number {
  color: var(--app-primary);
}

.referral-card .stat-label {
  color: var(--app-text-secondary);
  font-size: 12px;
}

.share-buttons {
  display: flex;
  gap: 12px;
}

.share-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.share-btn.twitter {
  background: var(--app-surface-hover);
  border: 1px solid var(--app-border);
  color: var(--app-text);
}

.share-btn.twitter:hover {
  background: var(--app-border);
}

.share-btn.linkedin {
  background: rgba(0, 119, 181, 0.1);
  border: 1px solid rgba(0, 119, 181, 0.25);
  color: #0a66c2;
}

.share-btn.linkedin:hover {
  background: rgba(0, 119, 181, 0.18);
}

.share-btn.email {
  background: var(--app-surface-hover);
  border: 1px solid var(--app-border);
  color: var(--app-text-secondary);
}

.share-btn.email:hover {
  background: var(--app-border);
  color: var(--app-text);
}

/* How it works — flat white inset, tight top padding (no nested gray wash) */
.how-it-works {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
  border-radius: 12px;
  padding: 12px 16px 14px;
}

.how-it-works h4 {
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 0 0 12px;
}

.steps {
  display: flex;
  gap: 24px;
}

.step {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
}

.step-number {
  width: 28px;
  height: 28px;
  background: var(--fill-primary-soft, color-mix(in srgb, var(--app-primary) 12%, var(--app-surface)));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--app-primary);
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
}

.step p {
  color: var(--app-text-secondary);
  font-size: 14px;
  margin: 0;
}

/* Pricing Grid */
.pricing-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  padding-top: 14px;
}

.pricing-card {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
  border-radius: var(--card-radius, 12px);
  padding: 28px;
  position: relative;
  transition: border-color 0.2s, background 0.2s;
  display: flex;
  flex-direction: column;
  box-shadow: none;
}

.pricing-card:hover {
  border-color: var(--app-border-focus);
  box-shadow: none;
}

.pricing-card.featured {
  border-color: var(--app-primary);
  background: var(--fill-primary-soft, color-mix(in srgb, var(--app-primary) 12%, var(--app-surface)));
  box-shadow: none;
}

.pricing-card.featured:hover {
  box-shadow: none;
}

.featured-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--app-primary);
  color: var(--app-primary-text);
  font-size: 12px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  box-shadow: none;
}

.pricing-header h3 {
  color: var(--app-text);
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 4px;
}

.pricing-card.featured .pricing-header h3 {
  color: #5b21b6;
}

.pricing-desc {
  color: var(--app-text-secondary);
  font-size: 14px;
  margin-bottom: 20px;
}

.pricing-card.featured .pricing-desc {
  color: #6d28d9;
}

.pricing-price {
  margin-bottom: 24px;
}

.price {
  font-size: 36px;
  font-weight: 700;
  color: var(--app-text);
}

.pricing-card.featured .price {
  color: #5b21b6;
}

.pricing-features {
  list-style: none;
  padding: 0;
  margin: 0 0 28px;
  flex-grow: 1;
}

.pricing-features li {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--app-text-secondary);
  font-size: 14px;
  margin-bottom: 12px;
}

.pricing-card.featured .pricing-features li {
  color: #5b21b6;
}

.pricing-features i {
  color: #22c55e;
  font-size: 12px;
}

.pricing-card.featured .pricing-features i {
  color: #6d28d9;
}

/* Buttons */
.btn {
  width: 100%;
  padding: 14px;
  min-height: var(--control-height, 44px);
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
  border: none;
}

.btn-primary {
  background: var(--app-primary);
  color: var(--app-primary-text);
}

.btn-primary:hover {
  background: var(--app-primary-hover);
}

.btn-secondary {
  background: var(--fill-primary-soft, color-mix(in srgb, var(--app-primary) 12%, var(--app-surface)));
  border: 1px solid var(--app-border);
  color: var(--app-text);
}

.btn-secondary:hover {
  background: var(--fill-primary-soft-strong, color-mix(in srgb, var(--app-primary) 18%, var(--app-surface)));
  border-color: var(--app-border);
}

/* Responsive */
@media (max-width: 900px) {
  .pricing-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .pricing-card.featured {
    order: -1;
  }

  .referral-stats {
    flex-direction: row;
  }

  .share-buttons {
    flex-direction: column;
  }

  .steps {
    flex-direction: column;
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .account-edit-layout {
    flex-direction: column;
  }

  .account-form-panel {
    flex: 1 1 auto;
    max-width: none;
  }

  .account-preview-panel {
    min-height: 200px;
  }

  /* Hide credits card on mobile (shown in header) */
  .desktop-only {
    display: none !important;
  }

  /* Compact Pricing Cards */
  .pricing-grid {
    display: flex;
    flex-direction: row;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    gap: 12px;
    padding-top: 14px;
    padding-bottom: 8px;
    -webkit-overflow-scrolling: touch;
  }

  .pricing-card {
    flex: 0 0 260px;
    scroll-snap-align: center;
    padding: 16px;
  }

  .pricing-card.featured {
    order: -1;
  }

  .featured-badge {
    font-size: 10px;
    padding: 4px 10px;
    top: -10px;
  }

  .pricing-header {
    margin-bottom: 8px;
  }

  .pricing-header h3 {
    font-size: 16px;
    margin-bottom: 2px;
  }

  .pricing-desc {
    font-size: 12px;
    margin-bottom: 12px;
  }

  .pricing-price {
    margin-bottom: 12px;
  }

  .price {
    font-size: 28px;
  }

  .pricing-features {
    margin-bottom: 16px;
  }

  .pricing-features li {
    font-size: 12px;
    margin-bottom: 6px;
    gap: 8px;
  }

  .pricing-features i {
    font-size: 10px;
  }

  .btn {
    padding: 10px;
    font-size: 13px;
  }
}

@media (max-width: 600px) {
  .account-container {
    padding: 0;
    padding-bottom: 24px;
    background: transparent;
  }

  /* Section headers — left-aligned with card leading edge */
  .section {
    margin-bottom: 24px;
    text-align: left;
  }

  .section h2 {
    font-size: 22px;
    font-weight: 700;
  }

  .section-subtitle {
    font-size: 15px;
    margin-bottom: 16px;
    color: var(--text-secondary);
  }

  /* Compact Pricing Cards — hairline, no elevation */
  .pricing-grid {
    flex-direction: row;
    gap: 10px;
  }

  .pricing-card {
    flex: 0 0 220px;
    padding: 14px;
    background: var(--app-surface);
    border: 1px solid var(--app-border);
    border-radius: var(--card-radius, 12px);
    box-shadow: none;
  }

  .pricing-card.featured {
    box-shadow: none;
  }

  .pricing-header h3 {
    font-size: 15px;
  }

  .pricing-desc {
    font-size: 11px;
    margin-bottom: 10px;
  }

  .price {
    font-size: 24px;
  }

  .pricing-features li {
    font-size: 11px;
    margin-bottom: 5px;
  }

  .btn {
    padding: 10px;
    font-size: 13px;
    min-height: var(--control-height, 44px);
  }

  /* Referral: one white card on page bg */
  .referral-section {
    padding: 16px;
    border-radius: var(--card-radius, 12px);
    margin-bottom: 0;
    background: var(--app-surface);
    border: 1px solid var(--app-border);
    box-shadow: none;
    overflow: visible;
  }

  .referral-header {
    flex-direction: row;
    align-items: flex-start;
    text-align: left;
    gap: 12px;
    margin-bottom: 16px;
  }

  .referral-header h2 {
    font-size: 17px;
  }

  .referral-header .section-subtitle {
    font-size: 14px;
  }

  .referral-icon {
    width: 40px;
    height: 40px;
  }

  .referral-icon i {
    font-size: 18px;
  }

  .referral-card {
    padding: 0;
    margin-bottom: 16px;
    background: transparent;
    border: none;
    box-shadow: none;
    overflow: visible;
  }

  .referral-link-section {
    margin-bottom: 16px;
  }

  .referral-link-section label {
    font-size: 13px;
    text-align: left;
    color: var(--text-secondary);
    opacity: 1;
    font-weight: 600;
  }

  .referral-link-box {
    flex-direction: column;
    gap: 8px;
  }

  .referral-link-box input {
    width: 100%;
    font-size: 12px;
    padding: 10px 12px;
    background: var(--app-surface);
    border: 1px solid var(--app-border);
    color: var(--app-text);
  }

  .copy-btn {
    width: 100%;
    justify-content: center;
    padding: 10px;
    font-size: 12px;
    box-shadow: none;
  }

  .referral-stats {
    flex-direction: row;
    gap: 8px;
    margin-bottom: 16px;
  }

  .referral-card .stat-item {
    padding: 10px 8px;
    flex: 1;
    background: var(--app-surface);
    border: 1px solid var(--app-border);
    box-shadow: none;
  }

  .referral-card .stat-item.highlight {
    background: color-mix(in srgb, var(--app-primary) 10%, var(--app-surface));
    border-color: color-mix(in srgb, var(--app-primary) 35%, var(--app-border));
  }

  .referral-card .stat-number {
    color: var(--app-text);
  }

  .referral-card .stat-label {
    font-size: 11px;
    font-weight: 500;
    color: var(--text-secondary);
    opacity: 1;
  }

  .stat-number {
    font-size: 18px;
  }

  .stat-label {
    font-size: 10px;
  }

  .share-buttons {
    flex-direction: row;
    gap: 8px;
  }

  .share-btn {
    padding: 8px 10px;
    font-size: 11px;
    flex: 1;
    box-shadow: none;
    border: 1px solid var(--app-border);
  }

  .share-btn i {
    font-size: 12px;
  }

  .share-btn span {
    display: none;
  }

  .how-it-works {
    padding: 12px 14px;
    background: var(--app-surface);
    border: 1px solid var(--app-border);
    border-radius: 10px;
    box-shadow: none;
    text-align: left;
  }

  .how-it-works h4 {
    font-size: 11px;
    margin: 0 0 10px;
    text-align: left;
    color: var(--text-secondary);
    opacity: 1;
  }

  .steps {
    gap: 10px;
    flex-direction: column;
  }

  .step {
    gap: 8px;
    align-items: flex-start;
  }

  .step-number {
    width: 22px;
    height: 22px;
    font-size: 10px;
  }

  .step p {
    font-size: 12px;
    color: var(--app-text);
    opacity: 0.78;
    line-height: 1.35;
    text-align: left;
  }

  .referral-header .section-subtitle {
    font-size: 12px;
    color: var(--app-text);
    opacity: 0.72;
  }

  .section-subtitle .highlight {
    opacity: 1;
  }
}
</style>
